import db from '../../../../services/firebase'
import {
  collection,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore'

class CloudDataService {
  flattenJsonData = (data) => {
    if (typeof data !== 'object' || data === null) {
      return data
    }
    
    if (Array.isArray(data)) {
      const flattened = {}
      data.forEach((item, index) => {
        if (typeof item === 'object' && item !== null) {
          flattened[`item_${index}`] = this.flattenJsonData(item)
        } else {
          flattened[`item_${index}`] = item
        }
      })
      return flattened
    }
    
    const flattened = {}
    for (const [key, value] of Object.entries(data)) {
      if (typeof value === 'object' && value !== null && Array.isArray(value)) {
        flattened[key] = this.flattenJsonData(value)
      } else if (typeof value === 'object' && value !== null) {
        flattened[key] = this.flattenJsonData(value)
      } else {
        flattened[key] = value
      }
    }
    
    return flattened
  }

  getClouds = (tenantId, callback) => {
    if (!tenantId) {
      return null
    }

    const tenantRef = doc(db, 'tenants', tenantId)
    const cloudsRef = collection(tenantRef, 'cloud')

    const unsubscribe = onSnapshot(cloudsRef, (snapshot) => {
      const cloudsData = snapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }))
      callback(cloudsData)
    })

    return unsubscribe
  }

  getCloudById = async (tenantId, cloudId) => {
    if (!tenantId || !cloudId) {
      return null
    }

    try {
      const tenantRef = doc(db, 'tenants', tenantId)
      const cloudRef = doc(tenantRef, 'cloud', cloudId)
      const cloudSnap = await getDoc(cloudRef)

      if (cloudSnap.exists()) {
        return {
          ...cloudSnap.data(),
          id: cloudSnap.id,
        }
      } else {
        console.log('No such cloud!')
        return null
      }
    } catch (error) {
      throw error
    }
  }

  uploadJsonToCloud = async (tenantId, cloudId, jsonData, arrayFieldName = 'data') => {
    if (!tenantId || !cloudId) {
      return false
    }

    try {
      const tenantRef = doc(db, 'tenants', tenantId)
      const cloudRef = doc(tenantRef, 'cloud', cloudId)
      
      const cloudSnap = await getDoc(cloudRef)
      let currentArray = []
      
      if (cloudSnap.exists()) {
        const data = cloudSnap.data()
        currentArray = data[arrayFieldName] || []
      }
      
      let itemsToAdd = []
      
      if (Array.isArray(jsonData)) {
        itemsToAdd = jsonData.map(item => this.flattenJsonData(item))
      } else {
        itemsToAdd = [this.flattenJsonData(jsonData)]
      }
      
      currentArray.push(...itemsToAdd)
      
      await updateDoc(cloudRef, {
        [arrayFieldName]: currentArray,
        last_updated: serverTimestamp(),
      })
      
      return true
    } catch (error) {
      throw error
    }
  }

  replaceArrayInCloud = async (tenantId, cloudId, newArray, arrayFieldName = 'data') => {
    if (!tenantId || !cloudId) {
      return false
    }

    try {
      const tenantRef = doc(db, 'tenants', tenantId)
      const cloudRef = doc(tenantRef, 'cloud', cloudId)
      
      const validArray = Array.isArray(newArray) ? newArray : [newArray]
      
      const flattenedArray = validArray.map(item => this.flattenJsonData(item))
      
      await updateDoc(cloudRef, {
        [arrayFieldName]: flattenedArray,
        last_updated: serverTimestamp(),
      })
      
      return true
    } catch (error) {
      throw error
    }
  }

  getArrayFromCloud = async (tenantId, cloudId, arrayFieldName = 'data') => {
    if (!tenantId || !cloudId) {
      return null
    }

    try {
      const tenantRef = doc(db, 'tenants', tenantId)
      const cloudRef = doc(tenantRef, 'cloud', cloudId)
      const cloudSnap = await getDoc(cloudRef)

      if (cloudSnap.exists()) {
        const data = cloudSnap.data()
        return data[arrayFieldName] || []
      } else {
        return []
      }
    } catch (error) {
      throw error
    }
  }

  addCloud = async (tenantId, cloudData) => {
    if (!tenantId) {
      return null
    }

    try {
      const tenantRef = doc(db, 'tenants', tenantId)
      const cloudsRef = collection(tenantRef, 'cloud')
      const docRef = await addDoc(cloudsRef, {
        ...cloudData,
        created_at: serverTimestamp(),
        last_scanned: null,
      })
      return docRef.id
    } catch (error) {
      throw error
    }
  }

  updateCloud = async (tenantId, cloudId, updateData) => {
    if (!tenantId || !cloudId) {
      return false
    }

    try {
      const tenantRef = doc(db, 'tenants', tenantId)
      const cloudRef = doc(tenantRef, 'cloud', cloudId)
      await updateDoc(cloudRef, updateData)
      return true
    } catch (error) {
      throw error
    }
  }

  deleteCloud = async (tenantId, cloudId) => {
    if (!tenantId || !cloudId) {
      return false
    }

    try {
      const tenantRef = doc(db, 'tenants', tenantId)
      const cloudRef = doc(tenantRef, 'cloud', cloudId)
      await deleteDoc(cloudRef)
      return true
    } catch (error) {
      throw error
    }
  }

  getServiceAccountDataFromCloud = async (tenantId, cloudId) => {
    if (!tenantId || !cloudId) {
      return null
    }

    try {
      const tenantRef = doc(db, 'tenants', tenantId)
      const cloudRef = doc(tenantRef, 'cloud', cloudId)
      const cloudSnap = await getDoc(cloudRef)

      if (cloudSnap.exists()) {
        const data = cloudSnap.data()
        return data.serviceAccountData || null
      } else {
        return null
      }
    } catch (error) {
      throw error
    }
  }

  runCloudSploitScan = async (serviceAccountData, projectId) => {
    if (!serviceAccountData || !projectId) {
      return null
    }

    try {
      const functionUrl = `https://us-central1-slate-134a0.cloudfunctions.net/runCloudSploitScan`
      
      const requestData = {
        project_id: projectId,
        client_email: serviceAccountData.client_email,
        private_key: serviceAccountData.private_key
      }

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
        signal: AbortSignal.timeout(360000) 
      })

      let result
      try {
        result = await response.json()
      } catch (jsonError) {
        result = {
          success: false,
          error: `Server error (${response.status}): ${response.statusText}`
        }
      }

      if (!response.ok) {
        const errorMessage = result.error || result.message || `HTTP ${response.status}: ${response.statusText}`
        throw new Error(errorMessage)
      }
      return result
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Scan timeout: The scan took too long to complete. This may be due to a large number of resources in your GCP project.')
      } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to cloud function')
      } else if (error.message.includes('500')) {
        throw new Error('Server error: The scan failed due to internal server issues. This may be due to resource limits or complex project configurations.')
      }
      throw error
    }
  }

  runCloudSploitScanFromCloudId = async (tenantId, cloudId) => {
    if (!tenantId || !cloudId) {
      return null
    }

    try {
      const serviceAccountData = await this.getServiceAccountDataFromCloud(tenantId, cloudId)
      
      if (!serviceAccountData) {
        throw new Error('No service account data found in cloud document')
      }

      const scanResults = await this.runCloudSploitScan(serviceAccountData, serviceAccountData.project_id)
      
      if (scanResults && scanResults.success) {
        try {
          await this.updateCloudWithScanResults(tenantId, cloudId, scanResults, serviceAccountData.project_id)
        } catch (updateError) {
          console.error('Error updating cloud with scan results:', updateError)
        }
      }

      return scanResults
    } catch (error) {
      throw error
    }
  }

  updateCloudWithScanResults = async (tenantId, cloudId, scanResults, projectId) => {
    if (!tenantId || !cloudId) {
      return false;
    }
  
    try {
      const tenantRef = doc(db, 'tenants', tenantId);
      const cloudRef = doc(tenantRef, 'cloud', cloudId);
      
      const rawResults = scanResults.result?.results || scanResults.result || [];
      
      const sanitizedResults = rawResults.map(result => ({
        title: result.title ?? '',
        description: result.description ?? '',
        plugin: result.plugin ?? '',
        category: result.category ?? '',
        region: result.region ?? '',
        status: result.status ?? 'UNKNOWN',
        message: result.message ?? '',
        resource: result.resource ?? 'N/A'
      }));
  
      const groupedResults = {};
      sanitizedResults.forEach(result => {
        const checkId = `${result.title}|${result.description}|${result.plugin}|${result.category}`;
        if (!groupedResults[checkId]) {
          groupedResults[checkId] = {
            ...result,
            regions: new Set()
          };
        }
        groupedResults[checkId].regions.add(result.region);
      });
  
      const consolidatedResults = Object.values(groupedResults).map(({ region, ...grouped }) => ({
        ...grouped,
        regions: Array.from(grouped.regions)
      }));
  
      const failIssues = consolidatedResults.filter(result => result.status === 'FAIL').length;
      const totalIssues = consolidatedResults.length;
      
      const failPercentage = totalIssues > 0 ? (failIssues / totalIssues) * 100 : 0;
      
      let overallSeverity = 'Low';
      if (failPercentage >= 50) {
        overallSeverity = 'High';
      } else if (failPercentage >= 20) {
        overallSeverity = 'Medium';
      }
      
      const totalOpenIssues = failIssues;
      
      if (!projectId) {
        throw new Error('projectId is undefined');
      }
  
      const updateData = {
        scan_results: consolidatedResults,
        open_issues: totalOpenIssues.toString(),
        severity: overallSeverity,
        last_scanned: serverTimestamp(),
        last_updated: serverTimestamp(),
        account_id: projectId,
        type: 'cloud'
      };
  
      await updateDoc(cloudRef, updateData);
      return true;
    } catch (error) {
      throw error;
    }
  };

}

export default new CloudDataService()